import { RawTrack } from '@customer/store/apis/investigationsApi';
import { useAppDispatch, useAppSelector } from '@customer/store/hooks';
import {
  setCurrentTrack,
  useInvestigationEntity,
} from '@customer/store/slices/investigationsSlice';
import { GridProps } from '@dmc/ui/tables';
import { IRowNode, RowClassParams, RowClassRules } from 'ag-grid-community';
import { useCallback, useMemo, useRef } from 'react';
import { useParams } from 'react-router-dom';

type Return = Pick<
  GridProps<RawTrack>,
  'rowClassRules' | 'ensureNodeVisibleCallback' | 'ensureNodeVisibleKey' | 'onRowClick'
>;

export const useRawTrackRowSelection = (): Return => {
  const dispatch = useAppDispatch();

  const { investigationId } = useParams();
  const siteId = useAppSelector(state => state.auth.siteId);

  const { currentRawTrack } = useInvestigationEntity({
    siteId,
    investigationId,
  });
  const currentRawTrackId = currentRawTrack?.id;

  const rowClassRules = useMemo<RowClassRules<RawTrack>>(
    () => ({
      'dm-highlight-row': (params: RowClassParams<RawTrack>) => {
        return typeof currentRawTrackId === 'number' && params.data?.id === currentRawTrackId;
      },
    }),
    [currentRawTrackId],
  );

  const currentRawTrackIdRef = useRef<number | undefined>(currentRawTrackId);
  const ensureNodeVisibleKey = useRef<number>(0);
  const skipEnsureNodeVisible = useRef<boolean>(false);

  if (currentRawTrackIdRef.current !== currentRawTrackId) {
    // Keep the ref in sync with the currentRawTrackId.
    currentRawTrackIdRef.current = currentRawTrackId;

    // Increment the key to trigger the effect in CasAgGrid.
    if (!skipEnsureNodeVisible.current) {
      ensureNodeVisibleKey.current++;
    } else {
      // After skipping once, re-enable the ensureNodeVisible effect for future changes.
      skipEnsureNodeVisible.current = false;
    }
  }

  // This callback signature will change every time the currentRawTrackId changes.
  // If this signature change is accompanied by a new ensureNodeVisibleKey it will trigger the effect in the CasAgGrid component.
  const ensureNodeVisibleCallback = useCallback(
    (rowNode: IRowNode<RawTrack>): boolean => {
      return typeof currentRawTrackId === 'number' && rowNode.data?.id === currentRawTrackId;
    },
    [currentRawTrackId],
  );

  const onRowClick = useCallback(
    ({ id }: RawTrack) => {
      if (typeof siteId !== 'undefined' && typeof investigationId !== 'undefined') {
        /**
         * Temporarily disable the ensureNodeVisible effect to prevent it from trying to scroll to the clicked row.
         * When the new currentTrackId is received on the next render, the ensureNodeVisibleKey incrementation will be skipped,
         * leaving the grid scroll position unchanged.
         */
        skipEnsureNodeVisible.current = true;
        dispatch(setCurrentTrack({ siteId, investigationId, id }));
      }
    },
    [dispatch, siteId, investigationId],
  );

  return {
    rowClassRules,
    ensureNodeVisibleCallback,
    onRowClick,
    ensureNodeVisibleKey: ensureNodeVisibleKey.current,
  };
};

export default useRawTrackRowSelection;
